import { useEffect, useMemo, useState } from 'react';
import { verTrilhaDoEnsaio } from '../../api/repeticao';
import type { LinhaDaTrilha } from '../../api/repeticao';
import { acaoDaTrilha, dataHora, oQueMudouNaTrilha, plural, rotulo } from '../../formato';
import { useSessao } from '../../sessao';
import { Carregando, ErroDaApi, Escolha, TabelaDeLancamento } from '../../ui';

/**
 * A trilha do ensaio (RN-260): as linhas e a situação, da mais nova para a
 * mais velha, com a tentativa de cada registro. Recolhida até alguém abrir —
 * a leitura é pesada e quem interpreta quase nunca precisa dela. Só para
 * quem tem «Consultar auditoria» (a rota pede a mesma permissão).
 *
 * `versao` muda quando a ficha muda: aberta, a trilha relê.
 */
const ETAPAS = ['Condições', 'Pesagem úmida', 'Pesagem seca', 'Interpretação',
  'Repetição', 'Situação'];
const PRIMEIRAS = 20;

/**
 * O que a linha fez e o que mudou, por extenso (P-TXT-03/01, P-FMT-01/04):
 * o dicionário único da trilha mora em `formato.ts`, junto do ROTULOS. A
 * troca de situação vem do histórico como lançamento; aqui ela se lê como
 * «de → para».
 */
function oQueMudou(l: LinhaDaTrilha): string {
  return oQueMudouNaTrilha(l.tabela === 'ensaio_status_historico'
    ? { ...l, operacao: 'UPDATE' } : l);
}

export function TrilhaDoEnsaio({ ensaioId, versao = 0 }: { ensaioId: string; versao?: number }) {
  const { pode } = useSessao();
  const [aberta, setAberta] = useState(false);
  const [linhas, setLinhas] = useState<LinhaDaTrilha[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [etapa, setEtapa] = useState<string | null>(null);
  const [todas, setTodas] = useState(false);
  const permitido = pode('auditoria.consultar');

  useEffect(() => {
    if (!aberta || !permitido) return undefined;
    let vivo = true;
    verTrilhaDoEnsaio(ensaioId)
      .then((l) => { if (vivo) setLinhas(l); })
      .catch((e) => { if (vivo) setErro(e); });
    return () => { vivo = false; };
  }, [aberta, ensaioId, versao, permitido]);

  const filtradas = useMemo(
    () => (linhas ?? []).filter((l) => !etapa || l.etapa === etapa), [linhas, etapa]);
  if (!permitido) return null;
  const vistas = todas ? filtradas : filtradas.slice(0, PRIMEIRAS);

  return (
    <details className="bloco fe-bloco repeticao-trilha"
      onToggle={(e) => { const abriu = (e.currentTarget as HTMLDetailsElement).open; setAberta(abriu); }}>
      <summary className="fe-bloco-topo"><h2><span>Trilha do ensaio</span></h2></summary>
      <div className="fe-miolo">
        <ErroDaApi erro={erro} />
        {aberta && !linhas && !erro && <Carregando texto="Lendo a trilha" />}
        {linhas && (
          <>
            <Escolha rotulo="Etapa" valor={etapa} aoEscolher={setEtapa}
              opcoes={ETAPAS.map((x) => ({ valor: x, texto: x }))} />
            {filtradas.length === 0
              ? <p className="sub">Nada registrado nesta etapa.</p>
              : (
                <TabelaDeLancamento rotulo="Trilha do ensaio" classe="repeticao-trilha-tabela"
                  colunas={[
                    { texto: 'Quando' }, { texto: 'Quem' }, { texto: 'Etapa' },
                    { texto: 'Tentativa', num: true }, { texto: 'O quê' }, { texto: 'Motivo' },
                  ]}
                  linhas={vistas.map((l, i) => ({
                    chave: `${l.datahora}-${l.tabela}-${i}`,
                    titulo: <>{dataHora(l.datahora)} · {l.usuario ?? '—'}</>,
                    resultado: l.etapa,
                    semCartao: [0, 1, 2],
                    celulas: [
                      dataHora(l.datahora),
                      <>{l.usuario ?? '—'}{l.perfil ? <span className="dd-quem">{rotulo(l.perfil)}</span> : null}</>,
                      l.etapa,
                      l.tentativa ?? '—',
                      <>{acaoDaTrilha(l)}
                        {oQueMudou(l) ? <span className="dd-quem">{oQueMudou(l)}</span> : null}</>,
                      l.motivo ?? '—',
                    ],
                  }))} />
              )}
            {!todas && filtradas.length > PRIMEIRAS && (
              <button type="button" className="botao discreto"
                onClick={() => setTodas(true)}>
                Ver os {plural(filtradas.length, 'registro', 'registros')}
              </button>
            )}
          </>
        )}
      </div>
    </details>
  );
}
